import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
/**
 * Resolves a PostHog boolean feature flag exactly once and locks the result in for the session — a later flag
 * reload can no longer flip it mid-session. Returns `null` until the first resolution (a cached value read at mount,
 * PostHog reporting flags, or the timeout fallback firing), then the resolved boolean.
 *
 * Use this when a flag decides something that must stay stable for the whole session
 * rather than something that should react to live flag changes.
 *
 * Requires a `<PostHogProvider>` ancestor.
 */
export function useResolvedFeatureFlag(flag, options) {
    const { fallback = false, timeoutMs = 2000 } = options ?? {};
    const posthog = usePostHog();
    // After the first isFeatureEnabled() query, the PostHog SDK caches the value in the browser's localstorage.
    const [resolved, setResolved] = useState(() => posthog.isFeatureEnabled(flag) ?? null);
    useEffect(() => {
        // Already locked in from cache — no listener, no timer, nothing left that could change the answer.
        if (resolved !== null) {
            return undefined;
        }
        // `current ?? …` locks in the first resolution so a later flag reload can't change it mid-session.
        const resolve = (value) => setResolved((current) => (current === null ? value : current));
        const unsubscribe = posthog.onFeatureFlags(() => {
            resolve(!!posthog.isFeatureEnabled(flag));
        });
        // Fall back if PostHog is slow/unreachable so the caller is never blocked on flag evaluation.
        const timer = setTimeout(() => resolve(fallback), timeoutMs);
        return () => {
            unsubscribe();
            clearTimeout(timer);
        };
    }, [posthog, flag, fallback, timeoutMs, resolved]);
    return resolved;
}
//# sourceMappingURL=use-resolved-feature-flag.js.map